import { supabase } from '@/lib/supabase';
import type { UserOnboardingStatus, CoachName, ServiceResult } from '@/types/institutional';

// ============================================================
// LECTURA
// ============================================================

export async function getOnboardingStatus(
  userId: string
): Promise<UserOnboardingStatus | null> {
  const { data, error } = await supabase
    .from('user_onboarding_status')
    .select('*')
    .eq('user_id', userId)
    .single();

  if (error || !data) return null;
  return data as UserOnboardingStatus;
}

export async function isOnboardingCompleted(userId: string): Promise<boolean> {
  const status = await getOnboardingStatus(userId);
  return status?.onboarding_completed ?? false;
}

// ============================================================
// ESCRITURA
// ============================================================

export async function saveOnboardingStep(
  userId: string,
  updates: Partial<Pick<UserOnboardingStatus, 'business_description' | 'main_objective'>>
): Promise<ServiceResult<UserOnboardingStatus>> {
  const { data, error } = await supabase
    .from('user_onboarding_status')
    .upsert({ user_id: userId, ...updates }, { onConflict: 'user_id' })
    .select()
    .single();

  if (error) return { success: false, error: error.message };
  return { success: true, data: data as UserOnboardingStatus };
}

export async function completeOnboarding(
  userId: string,
  payload: {
    business_description: string;
    main_objective: string;
    first_selected_coach: CoachName;
  }
): Promise<ServiceResult<UserOnboardingStatus>> {
  const { data, error } = await supabase
    .from('user_onboarding_status')
    .upsert(
      {
        user_id: userId,
        ...payload,
        onboarding_completed: true,
        completed_at: new Date().toISOString(),
      },
      { onConflict: 'user_id' }
    )
    .select()
    .single();

  if (error) return { success: false, error: error.message };
  return { success: true, data: data as UserOnboardingStatus };
}
